import type {
  BillingDashboardApiItem,
  BillingDashboardInvoicedVsPaidPoint,
  BillingOutstandingInvoiceApiItem,
} from 'src/redux/billing/billing.types';
import type { BillingFinancialOverview, BillingMonthlySeriesPoint, PastDueInvoiceRow } from './billing-dashboard.types';

function formatMonthLabel(ym: string): string {
  const parts = ym.split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  if (!y || !m) return ym;
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

function mapInvoicedVsPaidPoints(points: BillingDashboardInvoicedVsPaidPoint[]): BillingMonthlySeriesPoint[] {
  return points.map((p) => ({
    monthLabel: formatMonthLabel(p.month),
    invoiced: p.invoiced,
    collected: p.paid,
  }));
}

export function mapBillingDashboardApiToOverview(
  item: BillingDashboardApiItem,
  overdueAmountFromInvoices: number
): BillingFinancialOverview {
  const barSeries = mapInvoicedVsPaidPoints(item.charts.invoiced_vs_paid_last_3_months ?? []);
  const lineSeries = mapInvoicedVsPaidPoints(item.charts.invoiced_vs_paid_mom ?? []);
  const m = item.metrics;

  return {
    totalBilledCurrentPeriod: m.amount_invoiced ?? 0,
    totalCollected: m.amount_collected ?? 0,
    outstandingBalance: m.outstanding_amount ?? 0,
    overdueAmount: overdueAmountFromInvoices,
    priorMonthBilled: 0,
    priorMonthCollected: 0,
    currentMonthLabel: '',
    priorMonthLabel: '',
    invoicedPercentChangeVsPrior: m.percent_change_invoiced_mom ?? null,
    collectedPercentChangeVsPrior: null,
    monthlySeries: barSeries,
    monthlySeriesTrend: lineSeries.length > 0 ? lineSeries : undefined,
    snapshotUpdatedAt: item.snapshot?.updated_at ?? null,
  };
}

export function mapOutstandingInvoiceApiToRow(row: BillingOutstandingInvoiceApiItem): PastDueInvoiceRow {
  return {
    qboInvoiceId: row.qbo_invoice_id,
    internalInvoiceId: row.internal_invoice_id,
    invoiceNumber: row.doc_number?.trim() ? row.doc_number : '—',
    clientName: row.customer_name?.trim() ? row.customer_name : '—',
    dueDate: row.due_date ?? '',
    daysOverdue: row.days_overdue ?? 0,
    amount: row.balance ?? 0,
  };
}
